import {
  Box,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiActivity,
  FiEdit,
  FiGrid,
  FiList,
  FiLock,
  FiMapPin,
  FiPlay,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUnlock,
  FiWifi,
  FiX,
} from "react-icons/fi"

import { OpenAPI, ReadersService } from "@/client"
import type {
  ApiError,
  ReaderModelEnum,
  ReaderRead,
  ReaderRoleEnum,
  ReaderStateEnum,
} from "@/client"
import ConfirmDialog from "@/components/Common/ConfirmDialog"
import {
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
  VisionSelect,
} from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import { InputGroup } from "@/components/ui/input-group"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import { OrganizationContext, isAllOrgs } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/readers")({
  component: Readers,
})

const getStateColor = (state: ReaderStateEnum | undefined) => {
  switch (state) {
    case "INVENTORY":
      return "green"
    case "CONFIGURATION":
      return "blue"
    case "DATADUMP":
      return "purple"
    case "STANDBY":
      return "gray"
    case "ERROR":
      return "red"
    case "MAINTENANCE":
      return "orange"
    default:
      return "gray"
  }
}

const getRoleIcon = (role: ReaderRoleEnum) => {
  switch (role) {
    case "INITIALIZER":
      return "🔧"
    case "INVENTORY":
      return "📦"
    case "COLLECTOR":
      return "🗂️"
    case "MONITOR":
      return "📊"
    default:
      return "📡"
  }
}

const isMqttConnected = (reader: ReaderRead) => {
  if (!reader.mqtt_credentials_active || !reader.mqtt_last_seen) return false
  const lastSeen = new Date(reader.mqtt_last_seen).getTime()
  return Date.now() - lastSeen < 3 * 60 * 1000 // 3 minutes
}

function Readers() {
  const { t } = useTranslation(["readers", "common"])
  const sc = useSemanticColors()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { currentOrganization, organizations } = useContext(OrganizationContext)
  const [viewMode, setViewMode] = useState<"grid" | "table">(
    () =>
      (localStorage.getItem("readers-view-mode") as "grid" | "table") || "grid",
  )
  const effectiveViewMode: "grid" | "table" = isMobile ? "grid" : viewMode
  const updateViewMode = (mode: "grid" | "table") => {
    setViewMode(mode)
    localStorage.setItem("readers-view-mode", mode)
  }
  const [readerToDelete, setReaderToDelete] = useState<ReaderRead | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false)
  const [search, setSearch] = useState("")
  const [readerPage, setReaderPage] = useState(1)
  const readerPageSize = 50
  const [modelFilter, setModelFilter] = useState("")
  const [roleFilter, setRoleFilter] = useState("")
  const [stateFilter, setStateFilter] = useState("")
  const [sortBy, setSortBy] = useState("")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc")

  const toggleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(column)
      setSortOrder("asc")
    }
  }

  const availableModels: ReaderModelEnum[] = ["FX7500", "FX9600", "AR"]
  const availableRoles: ReaderRoleEnum[] = [
    "INITIALIZER",
    "INVENTORY",
    "COLLECTOR",
    "MONITOR",
  ]
  const availableStates: ReaderStateEnum[] = [
    "INVENTORY",
    "CONFIGURATION",
    "DATADUMP",
    "STANDBY",
    "ERROR",
    "MAINTENANCE",
  ]

  const hasFilter = search || modelFilter || roleFilter || stateFilter
  const clearFilters = () => {
    setSearch("")
    setModelFilter("")
    setRoleFilter("")
    setStateFilter("")
    setReaderPage(1)
  }

  const {
    data: readersResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "readers",
      currentOrganization?.id,
      readerPage,
      readerPageSize,
      search,
      modelFilter,
      roleFilter,
      stateFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      if (!currentOrganization) return { data: [], count: 0 }
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const params = new URLSearchParams({
        skip: String((readerPage - 1) * readerPageSize),
        limit: String(readerPageSize),
      })
      if (search) params.set("search", search)
      if (modelFilter) params.set("model", modelFilter)
      if (roleFilter) params.set("role", roleFilter)
      if (stateFilter) params.set("state", stateFilter)
      if (sortBy) {
        params.set("sort_by", sortBy)
        params.set("sort_order", sortOrder)
      }
      const url = isAllOrgs(currentOrganization)
        ? `${base}/api/v1/readers/paginated?${params}`
        : `${base}/api/v1/organizations/${currentOrganization.id}/readers/paginated?${params}`
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return { data: [], count: 0 }
      return res.json() as Promise<{ data: ReaderRead[]; count: number }>
    },
    enabled: !!currentOrganization,
    refetchInterval: 30000,
  })

  const filteredReaders = readersResult?.data ?? []
  const readersCount = readersResult?.count ?? 0

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ReadersService.deleteReader({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["readers"] })
      showToast.showSuccessToast(t("deleted"))
    },
    onError: (err: ApiError) => {
      const errDetail =
        (err.body as { detail?: string })?.detail || t("errorLoading")
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const handleDelete = (reader: ReaderRead) => {
    setReaderToDelete(reader)
  }

  // No bulk-delete endpoint for readers — delete sequentially to avoid firing
  // a connection-hungry burst at the DB pool; per-item failures are counted.
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      let failed = 0
      for (const id of ids) {
        try {
          await ReadersService.deleteReader({ id })
        } catch {
          failed++
        }
      }
      return { total: ids.length, failed }
    },
    onSuccess: ({ total, failed }) => {
      queryClient.invalidateQueries({ queryKey: ["readers"] })
      setSelectedIds(new Set())
      setConfirmBulkDelete(false)
      if (failed > 0) {
        showToast.showErrorToast(
          t("bulkDeletePartial", {
            success: total - failed,
            failed,
            defaultValue: "{{success}} deleted, {{failed}} failed",
          }),
        )
      } else {
        showToast.showSuccessToast(
          t("bulkDeleted", {
            count: total,
            defaultValue: "{{count}} reader(s) deleted",
          }),
        )
      }
    },
  })

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const allSelected =
    filteredReaders.length > 0 &&
    filteredReaders.every((r) => selectedIds.has(r.id))
  const toggleSelectAll = () => {
    setSelectedIds(
      allSelected ? new Set() : new Set(filteredReaders.map((r) => r.id)),
    )
  }

  if (!currentOrganization) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("common:org.noOrg")}
        </Heading>
        <Text color={sc.textMuted}>{t("common:org.selectPrompt")}</Text>
      </VisionCard>
    )
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  if (error && (error as ApiError).status !== 404) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("errorLoading")}
        </Heading>
        <Text color={sc.textMuted}>{t("errorLoadingDesc")}</Text>
      </VisionCard>
    )
  }

  const renderGridView = () => (
    <Grid
      templateColumns={{
        base: "minmax(0, 1fr)",
        sm: "repeat(2, minmax(0, 1fr))",
        md: "repeat(2, minmax(0, 1fr))",
        lg: "repeat(3, minmax(0, 1fr))",
      }}
      gap={{ base: 3, md: 4, lg: 6 }}
    >
      {filteredReaders.map((reader) => (
        <VisionCard key={reader.id} variant="gradient">
          <Flex direction="column" h="100%">
            <Flex justify="space-between" align="flex-start" mb={4}>
              <Box flex="1" minW={0}>
                <Flex align="center" gap={2} mb={2} minW={0}>
                  <Heading size="md" color={sc.heading} truncate>
                    {reader.name}
                  </Heading>
                  <Text fontSize="2xl">{getRoleIcon(reader.role)}</Text>
                </Flex>
                <HStack gap={2} mb={2} flexWrap="wrap">
                  <Badge
                    colorScheme={getStateColor(reader.observed_state)}
                    size="sm"
                  >
                    {reader.observed_state}
                  </Badge>
                  <Badge colorScheme="purple" size="sm">
                    {reader.device_model}
                  </Badge>
                  <Badge
                    colorPalette={
                      !reader.mqtt_credentials_active
                        ? "gray"
                        : isMqttConnected(reader)
                          ? "green"
                          : "red"
                    }
                    size="sm"
                  >
                    {reader.mqtt_credentials_active ? (
                      <FiLock style={{ marginRight: "4px" }} />
                    ) : (
                      <FiUnlock style={{ marginRight: "4px" }} />
                    )}
                    {!reader.mqtt_credentials_active
                      ? `No ${t("table.mqtt")}`
                      : isMqttConnected(reader)
                        ? t("table.mqttConnected")
                        : t("table.mqttDisconnected")}
                  </Badge>
                </HStack>
                <VStack align="start" gap={1} mt={3}>
                  {reader.mac_address && (
                    <HStack gap={2}>
                      <FiWifi fontSize="14px" color={sc.textMuted} />
                      <Text color={sc.textSecondary} fontSize="sm">
                        {reader.mac_address}
                      </Text>
                    </HStack>
                  )}
                  <HStack gap={2}>
                    <FiMapPin fontSize="14px" color={sc.textMuted} />
                    <Text color={sc.textSecondary} fontSize="sm">
                      {reader.lat != null && reader.lon != null
                        ? `${reader.lat.toFixed(6)}, ${reader.lon.toFixed(6)}`
                        : "—"}
                    </Text>
                  </HStack>
                  <HStack gap={2}>
                    <FiActivity fontSize="14px" color={sc.textMuted} />
                    <Text color={sc.textSecondary} fontSize="sm">
                      {t("table.role")}: {reader.role}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
              <IconBox variant="glass" size="md">
                <FiWifi fontSize="20px" color={sc.iconColor} />
              </IconBox>
            </Flex>

            <Flex gap={2} mt="auto">
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({
                    to: "/reader-control",
                    search: { readerId: reader.id },
                  })
                }
                flex="1"
              >
                <FiPlay /> {t("common:actions.control")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({
                    to: "/reader-form",
                    search: { readerId: reader.id },
                  })
                }
                flex="1"
              >
                <FiEdit /> {t("common:actions.edit")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                colorPalette="red"
                onClick={() => handleDelete(reader)}
              >
                <FiTrash2 />
              </VisionButton>
            </Flex>
          </Flex>
        </VisionCard>
      ))}

      {filteredReaders.length === 0 && (
        <VisionCard variant="outline">
          <Flex direction="column" align="center" justify="center" py={8}>
            <IconBox variant="glass" size="lg" mb={4}>
              <FiWifi fontSize="24px" color={sc.iconColor} />
            </IconBox>
            <Heading size="md" color={sc.heading} mb={2}>
              {t("noReaders")}
            </Heading>
            <Text color={sc.textMuted} textAlign="center" mb={4}>
              {t("noReadersDesc")}
            </Text>
            <VisionButton
              variant="brand"
              onClick={() =>
                navigate({
                  to: "/reader-form",
                  search: { readerId: undefined },
                })
              }
            >
              <FiPlus /> {t("addReader")}
            </VisionButton>
          </Flex>
        </VisionCard>
      )}
    </Grid>
  )

  const renderTableView = () => (
    <VisionCard p={{ base: 3, md: "22px" }}>
      {selectedIds.size > 0 && (
        <Flex
          justify="space-between"
          align="center"
          mb={3}
          p={2}
          borderRadius="10px"
          bg="rgba(255,100,100,0.08)"
          border="1px solid rgba(255,100,100,0.25)"
        >
          <Text fontSize="sm" color="red.300">
            {t("selected", {
              count: selectedIds.size,
              defaultValue: "{{count}} selected",
            })}
          </Text>
          <HStack gap={2}>
            <VisionButton
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
            >
              {t("common:actions.cancel")}
            </VisionButton>
            <VisionButton
              variant="ghost"
              size="sm"
              onClick={() => setConfirmBulkDelete(true)}
              style={{
                borderColor: "rgba(255,100,100,0.4)",
                color: "#FC8181",
              }}
            >
              <FiTrash2 /> {t("common:actions.deleteSelected")}
            </VisionButton>
          </HStack>
        </Flex>
      )}
      {filteredReaders.length === 0 ? (
        <Flex direction="column" align="center" py={10} gap={3}>
          <FiWifi fontSize="36px" color="gray" />
          <Text color={sc.textMuted} fontSize="sm">
            {t("noReaders")}
          </Text>
        </Flex>
      ) : (
        <Box overflowX="auto" maxW="100%">
          <Table.Root
            size={{ base: "sm", md: "md" }}
            style={{ minWidth: "800px" }}
          >
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader style={{ width: 40, minWidth: 40 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all readers"
                    style={{
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                      accentColor: "#34a1dd",
                    }}
                  />
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("name")}
                  _hover={{ color: sc.heading }}
                >
                  {t("common:table.name")}{" "}
                  {sortBy === "name" && (sortOrder === "asc" ? "▲" : "▼")}
                </Table.ColumnHeader>
                {isAllOrgs(currentOrganization) && (
                  <Table.ColumnHeader color={sc.tableHeaderColor}>
                    {t("common:filter.organisation")}
                  </Table.ColumnHeader>
                )}
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("device_model")}
                  _hover={{ color: sc.heading }}
                >
                  {t("table.model")}{" "}
                  {sortBy === "device_model" &&
                    (sortOrder === "asc" ? "▲" : "▼")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("mac_address")}
                  _hover={{ color: sc.heading }}
                >
                  {t("table.mac")}{" "}
                  {sortBy === "mac_address" &&
                    (sortOrder === "asc" ? "▲" : "▼")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("state")}
                  _hover={{ color: sc.heading }}
                >
                  {t("common:table.state")}{" "}
                  {sortBy === "state" && (sortOrder === "asc" ? "▲" : "▼")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("role")}
                  _hover={{ color: sc.heading }}
                >
                  {t("table.role")}{" "}
                  {sortBy === "role" && (sortOrder === "asc" ? "▲" : "▼")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.tableHeaderColor}>
                  {t("table.mqtt")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.tableHeaderColor}>
                  {t("common:table.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredReaders.map((reader) => (
                <Table.Row
                  key={reader.id}
                  cursor="pointer"
                  _hover={{ bg: sc.hoverBg }}
                  onClick={() =>
                    navigate({
                      to: "/reader-control",
                      search: { readerId: reader.id },
                    })
                  }
                >
                  <Table.Cell
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 40 }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(reader.id)}
                      onChange={() => toggleSelect(reader.id)}
                      aria-label={`Select ${reader.name}`}
                      style={{
                        width: 16,
                        height: 16,
                        cursor: "pointer",
                        accentColor: "#34a1dd",
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell color={sc.heading} fontWeight="500">
                    {reader.name}
                  </Table.Cell>
                  {isAllOrgs(currentOrganization) && (
                    <Table.Cell>
                      <Text color={sc.tableCellMuted} fontSize="sm">
                        {organizations.find(
                          (o) => o.id === reader.organisation_id,
                        )?.name || "—"}
                      </Text>
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <Badge colorScheme="purple" size="sm">
                      {reader.device_model}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell
                    color={sc.tableCellMuted}
                    fontFamily="mono"
                    fontSize="sm"
                  >
                    {reader.mac_address || "—"}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      colorScheme={getStateColor(reader.observed_state)}
                      size="sm"
                    >
                      {reader.observed_state}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                    {getRoleIcon(reader.role)} {reader.role}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      colorPalette={
                        !reader.mqtt_credentials_active
                          ? "gray"
                          : isMqttConnected(reader)
                            ? "green"
                            : "red"
                      }
                      size="sm"
                    >
                      {!reader.mqtt_credentials_active
                        ? t("common:status.none")
                        : isMqttConnected(reader)
                          ? t("table.mqttConnected")
                          : t("table.mqttDisconnected")}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={1} onClick={(e) => e.stopPropagation()}>
                      <VisionButton
                        variant="ghost"
                        size="sm"
                        title={t("common:actions.control")}
                        onClick={() =>
                          navigate({
                            to: "/reader-control",
                            search: { readerId: reader.id },
                          })
                        }
                      >
                        <FiPlay />
                      </VisionButton>
                      <VisionButton
                        variant="ghost"
                        size="sm"
                        title={t("common:actions.edit")}
                        onClick={() =>
                          navigate({
                            to: "/reader-form",
                            search: { readerId: reader.id },
                          })
                        }
                      >
                        <FiEdit />
                      </VisionButton>
                      <VisionButton
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(reader)}
                      >
                        <FiTrash2 />
                      </VisionButton>
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VisionCard>
  )

  return (
    <Box>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        gap={{ base: 3, md: 0 }}
        mb={{ base: 5, md: 8 }}
      >
        <Box minW={0}>
          <Heading size={{ base: "lg", md: "xl" }} color={sc.heading} mb={2}>
            {t("title")}
          </Heading>
          <Text color={sc.textMuted} fontSize={{ base: "sm", md: "md" }}>
            {t("subtitle", { orgName: currentOrganization.name })}
          </Text>
        </Box>
        <Flex gap={2} wrap="wrap" align="center">
          <Box display={{ base: "none", md: "block" }}>
            <HStack bg={sc.toggleBg} borderRadius="10px" p="2px">
              <Box
                as="button"
                p={2}
                borderRadius="8px"
                bg={viewMode === "grid" ? sc.toggleActiveBg : "transparent"}
                color={viewMode === "grid" ? sc.heading : sc.textMuted}
                _hover={{ color: sc.heading }}
                transition="all 0.2s"
                onClick={() => updateViewMode("grid")}
                title={t("common:actions.gridView")}
              >
                <FiGrid size={18} />
              </Box>
              <Box
                as="button"
                p={2}
                borderRadius="8px"
                bg={viewMode === "table" ? sc.toggleActiveBg : "transparent"}
                color={viewMode === "table" ? sc.heading : sc.textMuted}
                _hover={{ color: sc.heading }}
                transition="all 0.2s"
                onClick={() => updateViewMode("table")}
                title={t("common:actions.tableView")}
              >
                <FiList size={18} />
              </Box>
            </HStack>
          </Box>
          <VisionButton
            variant="brand"
            onClick={() =>
              navigate({ to: "/reader-form", search: { readerId: undefined } })
            }
          >
            <FiPlus />
            {t("addReader")}
          </VisionButton>
        </Flex>
      </Flex>

      <VisionCard mb={{ base: 4, md: 6 }} p={{ base: 3, md: "22px" }}>
        <Flex
          direction={{ base: "column", sm: "row" }}
          gap={2}
          align={{ base: "stretch", sm: "center" }}
        >
          <InputGroup startElement={<FiSearch color={sc.iconMuted} />} flex={1}>
            <VisionInput
              placeholder={t("searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          {hasFilter && (
            <VisionButton variant="ghost" onClick={clearFilters} size="sm">
              <FiX /> {t("common:actions.clearFilter")}
            </VisionButton>
          )}
        </Flex>
        <Grid
          templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
          gap={3}
          mt={3}
        >
          <Box>
            <Text fontSize="xs" color={sc.textMuted} fontWeight="bold" mb={1}>
              {t("form.model")}
            </Text>
            <VisionSelect
              value={modelFilter}
              onChange={(e) =>
                setModelFilter((e.target as HTMLSelectElement).value)
              }
            >
              <option value="">{t("common:filter.all")}</option>
              {availableModels.map((m) => (
                <option key={m} value={m}>
                  {t(`models.${m}`, m)}
                </option>
              ))}
            </VisionSelect>
          </Box>
          <Box>
            <Text fontSize="xs" color={sc.textMuted} fontWeight="bold" mb={1}>
              {t("form.role")}
            </Text>
            <VisionSelect
              value={roleFilter}
              onChange={(e) =>
                setRoleFilter((e.target as HTMLSelectElement).value)
              }
            >
              <option value="">{t("common:filter.all")}</option>
              {availableRoles.map((r) => (
                <option key={r} value={r}>
                  {t(`roles.${r}`, r)}
                </option>
              ))}
            </VisionSelect>
          </Box>
          <Box>
            <Text fontSize="xs" color={sc.textMuted} fontWeight="bold" mb={1}>
              {t("common:table.state")}
            </Text>
            <VisionSelect
              value={stateFilter}
              onChange={(e) =>
                setStateFilter((e.target as HTMLSelectElement).value)
              }
            >
              <option value="">{t("common:filter.all")}</option>
              {availableStates.map((s) => (
                <option key={s} value={s}>
                  {t(`states.${s}`, s)}
                </option>
              ))}
            </VisionSelect>
          </Box>
        </Grid>
      </VisionCard>

      {effectiveViewMode === "grid" ? renderGridView() : renderTableView()}

      <Flex mt={4} alignItems="center" justifyContent="space-between">
        <Text fontSize="sm" color={sc.textMuted}>
          {readersCount} {t("readers:title").toLowerCase()}
        </Text>
        {readersCount > readerPageSize && (
          <PaginationRoot
            page={readerPage}
            count={readersCount}
            pageSize={readerPageSize}
            onPageChange={(e) => setReaderPage(e.page)}
          >
            <HStack>
              <PaginationPrevTrigger />
              <PaginationItems />
              <PaginationNextTrigger />
            </HStack>
          </PaginationRoot>
        )}
      </Flex>

      <ConfirmDialog
        open={!!readerToDelete}
        onClose={() => setReaderToDelete(null)}
        onConfirm={() => {
          if (readerToDelete) deleteMutation.mutate(readerToDelete.id)
        }}
        title={t("common:confirm.deleteTitle", { type: t("readers:title") })}
        message={t("common:confirm.deleteWarning")}
        loading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        onConfirm={() => bulkDeleteMutation.mutate([...selectedIds])}
        title={t("common:confirm.deleteTitle", { type: t("readers:title") })}
        message={t("confirmBulkDelete", {
          count: selectedIds.size,
          defaultValue:
            "Delete {{count}} selected reader(s)? This cannot be undone.",
        })}
        loading={bulkDeleteMutation.isPending}
      />
    </Box>
  )
}
